import React, { useMemo, useRef } from "react";
import TextFormatPositionPanel from "../../../../../components/shared/textToolbar/TextFormatPositionPanel";
import {
  textFormatPatchToWatermark,
  watermarkToTextFormat,
} from "../../../watermark/Editor/watermarkTextFormatAdapter";
import {
  applyMarginAlongNearestEdge,
  getWatermarkFontStack,
  markEdgeGapsPx,
  marginFromNearestEdgeGap,
  measureWatermarkText,
  resolveMarginGapPx,
  resolveSingleMarkPosition,
  WATERMARK_CONTENT_TYPES,
  watermarkFontSizePx,
  watermarkLogoDisplaySize,
} from "../../../watermark/Popup/watermarkSettings";
import { CuteDropdown } from "./SignPdfCuteDropdown";
import {
  resolveSignPdfFontSizePx,
  resolveSignPdfLogoDisplaySize,
  signPdfFontSizeFieldsFromPx,
} from "./signPdfSettings";
import "./SignPdfPositionDropdown.css";

const STAGE_SELECTOR = ".sp-preview-canvas .transform-preview__canvas";

function readStageSize() {
  const el = document.querySelector(STAGE_SELECTOR);
  if (!el) return { width: 400, height: 400 };
  return {
    width: el.offsetWidth || 400,
    height: el.offsetHeight || 400,
  };
}

function resolveMarkMetrics(settings, stageW, stageH) {
  const isLogo =
    settings?.contentType === WATERMARK_CONTENT_TYPES.LOGO &&
    Boolean(settings?.logoUrl);
  if (isLogo) {
    return resolveSignPdfLogoDisplaySize(
      settings,
      stageW,
      stageH,
      Number(settings?.logoNaturalWidth) || 0,
      Number(settings?.logoNaturalHeight) || 0
    );
  }
  const fontSizePx = resolveSignPdfFontSizePx(settings, stageW, stageH);
  return measureWatermarkText(
    settings?.text || "Signature",
    fontSizePx,
    getWatermarkFontStack(settings?.fontId),
    settings?.fontWeight,
    {
      letterSpacing: settings?.letterSpacing,
      textScript: settings?.textScript,
    }
  );
}

/**
 * Sign PDF–owned Position control (9-grid + X/Y + margin).
 * Uses shared TextFormatPositionPanel UI, but patches Sign PDF placements only —
 * not the Watermark left-panel overlay host.
 */
export function SignPdfPositionDropdown({
  mark,
  openId,
  setOpenId,
  disabled = false,
  onPatch,
}) {
  const markRef = useRef(mark);
  markRef.current = mark;

  const positionValue = useMemo(
    () =>
      watermarkToTextFormat({
        ...(mark || {}),
        tile: "single",
      }),
    [mark]
  );

  const nearestMarginPct = useMemo(() => {
    const live = mark || {};
    const stage = readStageSize();
    const w = stage.width;
    const h = stage.height;
    const metrics = resolveMarkMetrics(live, w, h);
    const base = resolveSingleMarkPosition({
      position: live.position || "center",
      margin: live.margin,
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: Number(live.rotation) || 0,
      flushEdges: live.flushEdges !== false,
    });
    const centerX = base.x + (Number(live.offsetX) || 0);
    const centerY = base.y + (Number(live.offsetY) || 0);
    const gaps = markEdgeGapsPx({
      centerX,
      centerY,
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: Number(live.rotation) || 0,
    });
    const gapPx = resolveMarginGapPx(gaps, live.position || "center");
    return Math.round(marginFromNearestEdgeGap(gapPx, w, h) * 100);
  }, [mark]);

  const emitPlacementPatch = (nextSettings) => {
    const stage = readStageSize();
    const size = Number(nextSettings.size);
    const isLogo =
      nextSettings.contentType === WATERMARK_CONTENT_TYPES.LOGO &&
      Boolean(nextSettings.logoUrl);
    const patch = {
      position: nextSettings.position || "center",
      offsetX: Number(nextSettings.offsetX) || 0,
      offsetY: Number(nextSettings.offsetY) || 0,
      margin: Number.isFinite(Number(nextSettings.margin))
        ? Number(nextSettings.margin)
        : 0.06,
      rotation: Number(nextSettings.rotation) || 0,
    };
    if (Number.isFinite(size) && size > 0) {
      patch.size = size;
      const px = isLogo
        ? resolveSignPdfLogoDisplaySize(
            { ...nextSettings, size },
            stage.width,
            stage.height,
            Number(nextSettings.logoNaturalWidth) || 0,
            Number(nextSettings.logoNaturalHeight) || 0
          ).height
        : watermarkFontSizePx(size, stage.width, stage.height);
      if (Number.isFinite(px) && px > 0) {
        Object.assign(
          patch,
          signPdfFontSizeFieldsFromPx(px, stage.width, stage.height, {
            logo: isLogo,
          })
        );
      }
    }
    onPatch?.(patch);
  };

  const handlePositionChange = (nextFormat) => {
    if (disabled) return;
    const live = markRef.current || {};
    const positionPatch = {};
    const keys = [
      "position",
      "offsetX",
      "offsetY",
      "margin",
      "rotation",
      "size",
      "lockAspectRatio",
    ];
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(nextFormat, key)) {
        positionPatch[key] = nextFormat[key];
      }
    }
    if (nextFormat._meta?.margin !== undefined) {
      positionPatch.margin = nextFormat._meta.margin;
    }
    // Prefer host margin handler for nearest-edge — skip raw margin alone.
    if (
      positionPatch.margin !== undefined &&
      positionPatch.offsetX === undefined &&
      positionPatch.offsetY === undefined &&
      positionPatch.position === undefined
    ) {
      delete positionPatch.margin;
    }
    if (Object.keys(positionPatch).length === 0) return;
    const merged = textFormatPatchToWatermark(positionPatch, {
      ...live,
      tile: "single",
    });
    emitPlacementPatch(merged);
  };

  const handleMarginChange = (pct) => {
    if (disabled) return;
    const live = markRef.current || {};
    const stage = readStageSize();
    const w = stage.width;
    const h = stage.height;
    const metrics = resolveMarkMetrics(live, w, h);
    const base = resolveSingleMarkPosition({
      position: live.position || "center",
      margin: live.margin,
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: Number(live.rotation) || 0,
      flushEdges: live.flushEdges !== false,
    });
    const applied = applyMarginAlongNearestEdge({
      centerX: base.x + (Number(live.offsetX) || 0),
      centerY: base.y + (Number(live.offsetY) || 0),
      position: live.position || "center",
      nextMargin: Math.max(0, Math.min(0.4, pct / 100)),
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: Number(live.rotation) || 0,
      flushEdges: live.flushEdges !== false,
    });
    onPatch?.({
      margin: applied.margin,
      offsetX: applied.offsetX,
      offsetY: applied.offsetY,
    });
  };

  const positionLabel = String(mark?.position || "center")
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

  return (
    <CuteDropdown
      id="position"
      openId={openId}
      setOpenId={setOpenId}
      label="Position"
      title={`Position: ${positionLabel}`}
      disabled={disabled}
      menuWidth={312}
      menuMaxHeight={560}
      menuClassName="sp-dd__menu--position"
      display={<span className="sp-dd__truncate">Position</span>}
    >
      <div
        className="sp-pos-panel"
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <TextFormatPositionPanel
          className="tfp-panel--overlay sp-pos-panel__tfp"
          value={positionValue}
          onChange={handlePositionChange}
          disabled={disabled}
          stageSelector={STAGE_SELECTOR}
          positionSectionTitle="Position"
          enablePlacement
          enableSpacing={false}
          showSpacing={false}
          showMargin
          marginPercent={nearestMarginPct}
          onMarginPercentChange={handleMarginChange}
          sizeConfig={{
            formatDisplay: (v) => String(Math.round(v * 10)),
            parseDisplay: (display) => Number(display) / 10,
          }}
        />
      </div>
    </CuteDropdown>
  );
}

export default SignPdfPositionDropdown;
